import type { CommercialPlan } from '../../utils/client-reports/types';
import { LABEL_CLASS, Panel, StatusLegend } from './ReportBlocks';
import {
  ARRIVED_CLASS,
  formatPlanAmount,
  IN_TRANSIT_CLASS,
  NOT_DEPARTED_CLASS,
  PLAN_UNIT_LABELS,
} from './reportLayout';
import { useReportFormat } from './useReportFormat';

const CHART_HEIGHT = 168;

const DATE_BASIS_LABELS = {
  etd: 'semana de zarpe (ETD)',
  eta: 'semana de arribo (ETA)',
} as const;

// Real vs plan por semana, en la unidad y la fecha base del programa. La barra
// es lo real (arribado, en tránsito y por zarpar) y el recuadro punteado, el
// plan. Una línea marca el corte del reporte.
const CommercialPlanChart = ({ data }: { data: CommercialPlan }) => {
  const { t, number } = useReportFormat();
  const unit = t(PLAN_UNIT_LABELS[data.unit]);
  const amount = (value: number) => formatPlanAmount(value, data.unit, number);

  const hasData = (week: CommercialPlan['weeks'][0]) =>
    week.committed > 0 || week.arrived + week.inTransit + week.notDeparted > 0;
  const firstWeek = data.weeks.findIndex(hasData);
  const lastWeek = data.weeks.length - 1 - [...data.weeks].reverse().findIndex(hasData);
  let realAcc = 0;
  let planAcc = 0;
  const weeks = data.weeks
    .map((week) => {
      const real = week.arrived + week.inTransit + week.notDeparted;
      realAcc += real;
      planAcc += week.committed;
      return { ...week, real, realAcc, planAcc };
    })
    .slice(firstWeek, lastWeek + 1);
  const max = Math.max(...weeks.map((week) => Math.max(week.committed, week.real)), 1);

  const rowLabel = `${LABEL_CLASS} whitespace-nowrap pr-4`;
  const cell = 'min-w-12 flex-1 text-center text-xs';

  return (
    <Panel
      title={t('Real vs plan por {{basis}} · {{unit}}', {
        basis: t(DATE_BASIS_LABELS[data.dateBasis]),
        unit,
      })}
      aside={
        <StatusLegend withPlan withNotDeparted={weeks.some((week) => week.notDeparted > 0)} />
      }
    >
      <div className='overflow-x-auto'>
        <div className='grid min-w-fit grid-cols-[auto_1fr] gap-y-1.5'>
          <div />
          <div
            className='flex items-end gap-2 border-b border-default-300'
            style={{ height: CHART_HEIGHT + 32 }}
          >
            {weeks.map((week, index) => {
              const diff = week.real - week.committed;
              // Primera semana después del corte: ahí va la línea.
              const isFirstFuture = !week.isPast && (index === 0 || weeks[index - 1].isPast);
              const showDiff = week.isPast && week.committed > 0 && Math.round(diff * 10) !== 0;
              return (
                <div
                  key={week.week}
                  className='relative flex min-w-12 flex-1 flex-col items-center gap-1'
                >
                  {isFirstFuture ? (
                    <div className='absolute -left-1 bottom-0 top-0 border-l border-dashed border-default-400'>
                      <span className='absolute left-1.5 top-0 text-[10px] leading-none font-semibold uppercase tracking-wider text-default-500'>
                        {t('corte')}
                      </span>
                    </div>
                  ) : null}
                  {/* Real arriba y la diferencia contra el plan debajo, para que
                      las cifras grandes (cajas, kilos) no se monten. */}
                  <div className='flex flex-col items-center leading-tight'>
                    <span className='whitespace-nowrap text-[11px] font-semibold'>
                      {week.isPast || week.real > 0 ? amount(week.real) : '\u00a0'}
                    </span>
                    <span
                      className={`whitespace-nowrap text-[10px] font-medium ${
                        diff > 0 ? 'text-success-600' : 'text-danger-600'
                      }`}
                    >
                      {showDiff ? `${diff > 0 ? '+' : ''}${amount(diff)}` : '\u00a0'}
                    </span>
                  </div>
                  <div className='relative w-9' style={{ height: CHART_HEIGHT }}>
                    <div className='absolute bottom-0 flex w-full flex-col-reverse overflow-hidden rounded-t-sm'>
                      <div
                        className={ARRIVED_CLASS}
                        style={{ height: (week.arrived / max) * CHART_HEIGHT }}
                      />
                      <div
                        className={IN_TRANSIT_CLASS}
                        style={{ height: (week.inTransit / max) * CHART_HEIGHT }}
                      />
                      {week.notDeparted > 0 ? (
                        <div
                          className={`rounded-t-sm ${NOT_DEPARTED_CLASS}`}
                          style={{ height: (week.notDeparted / max) * CHART_HEIGHT }}
                        />
                      ) : null}
                    </div>
                    {week.committed > 0 ? (
                      <div
                        className='absolute bottom-0 -left-1.5 -right-1.5 rounded-t-sm border border-b-0 border-dashed border-default-500'
                        style={{ height: `${(week.committed / max) * 100}%` }}
                      />
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          <span className={`${rowLabel} self-center`}>{t('Semana')}</span>
          <div className='flex gap-2'>
            {weeks.map((week) => (
              <span key={week.week} className={`${cell} pt-1 font-semibold`}>
                {String(week.week).padStart(2, '0')}
              </span>
            ))}
          </div>
          <span className={rowLabel}>{t('Real ac.')}</span>
          <div className='flex gap-2'>
            {weeks.map((week) => (
              <span key={week.week} className={cell}>
                {week.isPast || week.real > 0 ? amount(week.realAcc) : '\u00a0'}
              </span>
            ))}
          </div>
          <span className={rowLabel}>{t('Plan ac.')}</span>
          <div className='flex gap-2'>
            {weeks.map((week) => (
              <span key={week.week} className={`${cell} text-default-500`}>
                {amount(week.planAcc)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default CommercialPlanChart;
